import { Button } from 'antd';
import { AlertTriangle, Inbox, RotateCw, SearchX } from 'lucide-react';

import { useI18n } from '../../i18n';

export const EmptyState = ({ title, text, icon, action, compact = false, variant }) => {
  const { t } = useI18n();
  const Icon = icon ?? (variant === 'search' ? SearchX : Inbox);
  return (
    <div className={`wm-empty${compact ? ' wm-empty--compact' : ''}`} role='status'>
      <div className='wm-empty__icon'>
        <Icon size={22} strokeWidth={1.75} />
      </div>
      <div className='wm-empty__title'>
        {title ?? (variant === 'search' ? t('states.emptySearch') : t('states.emptyTitle'))}
      </div>
      {(text ?? variant === 'search') && (
        <div className='wm-empty__text'>{text ?? t('states.emptySearchHint')}</div>
      )}
      {action && <div className='wm-empty__action'>{action}</div>}
    </div>
  );
};

export const ErrorState = ({ onRetry, compact = false, title, text }) => {
  const { t } = useI18n();
  return (
    <div className={`wm-empty wm-empty--error${compact ? ' wm-empty--compact' : ''}`} role='alert'>
      <div className='wm-empty__icon'>
        <AlertTriangle size={22} strokeWidth={1.75} />
      </div>
      <div className='wm-empty__title'>{title ?? t('states.errorTitle')}</div>
      <div className='wm-empty__text'>{text ?? t('states.errorHint')}</div>
      {onRetry && (
        <div className='wm-empty__action'>
          <Button onClick={onRetry} icon={<RotateCw size={14} />}>
            {t('common.retry')}
          </Button>
        </div>
      )}
    </div>
  );
};

export const Skeleton = ({ width = '100%', height = 14, radius, style, className = '' }) => (
  <span
    className={`wm-skeleton ${className}`}
    style={{ width, height, borderRadius: radius, ...style }}
    aria-hidden='true'
  />
);

export const TableSkeleton = ({ rows = 6 }) => (
  <div className='wm-skeleton-table' aria-busy='true'>
    {Array.from({ length: rows }).map((_, i) => (
      <div className='wm-skeleton-table__row' key={i}>
        <Skeleton width={`${70 - (i % 3) * 12}%`} />
        <Skeleton width='60%' />
        <Skeleton width='50%' />
        <Skeleton width='40%' />
      </div>
    ))}
  </div>
);

export const ChartSkeleton = ({ height = 280 }) => (
  <div style={{ height, display: 'flex', alignItems: 'flex-end', gap: 10, padding: '8px 4px 0' }} aria-busy='true'>
    {[42, 64, 50, 78, 58, 88, 70, 60, 82, 66, 74, 54].map((h, i) => (
      <Skeleton key={i} height={`${h}%`} style={{ flex: 1 }} radius={6} />
    ))}
  </div>
);

export const FormSkeleton = ({ fields = 4 }) => (
  <div aria-busy='true'>
    {Array.from({ length: fields }).map((_, i) => (
      <div key={i} style={{ marginBottom: 22 }}>
        <Skeleton width={110} height={12} style={{ marginBottom: 10 }} />
        <Skeleton height={36} radius={8} />
      </div>
    ))}
  </div>
);
